import { useEffect, useRef, useState } from 'react';

// Searchable single-select dropdown for large option lists.
//
// Props:
//   value           current value (string)
//   onChange(value) called with new value (or '' when cleared)
//   options         [{ value, label, count? }] OR strings
//   placeholder
//   width           default 260
//   renderOption    optional override for option label (string or JSX)
//   disabled
//
// Closes on outside-click. Arrow keys + Enter for keyboard navigation.
const MAX_VISIBLE = 50;

function normalizeOption(opt) {
  if (typeof opt === 'string') return { value: opt, label: opt };
  return opt;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  width = 260,
  renderOption,
  disabled,
}) {
  const [input, setInput]         = useState('');
  const [open, setOpen]           = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const normalized = options.map(normalizeOption);
  const selected = normalized.find((o) => String(o.value) === String(value));
  const renderLabel = (o) => (renderOption ? renderOption(o) : o.label);
  // A value with no matching option still has to READ as set. Shared links
  // carry filters the current tab may have no rows for (a location with zero
  // gaps, say), and the facet list only contains options that match something
  // — showing the placeholder there makes an applied filter look cleared and
  // "0 records" look like a broken link. Fall back to the raw value.
  const displayText = selected ? renderLabel(selected) : (value ? String(value) : '');

  const q = input.trim().toLowerCase();
  const filtered = q
    ? normalized.filter(
        (o) =>
          String(renderLabel(o)).toLowerCase().includes(q) ||
          String(o.value).toLowerCase().includes(q),
      )
    : normalized;
  const visible = filtered.slice(0, MAX_VISIBLE);

  const pick = (opt) => {
    onChange(opt ? String(opt.value) : '');
    setInput('');
    setOpen(false);
    setHighlight(0);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && visible[highlight]) {
      e.preventDefault();
      pick(visible[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setInput('');
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width }}>
      <div style={{ display: 'flex' }}>
        <input
          type="text"
          value={open ? input : displayText}
          onChange={(e) => { setInput(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => { setOpen(true); setInput(''); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          style={{ width: '100%', paddingRight: value ? 24 : 8 }}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={() => pick(null)}
            title="Clear"
            style={{
              position: 'absolute', right: 4, top: 6,
              border: 'none', background: 'transparent',
              cursor: 'pointer', fontSize: 14, color: '#888',
              lineHeight: 1, padding: '2px 6px',
            }}
          >
            ×
          </button>
        )}
      </div>

      {open && visible.length > 0 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
            background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
            maxHeight: 300, overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {visible.map((o, i) => (
            <div
              key={o.value}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '6px 10px',
                fontSize: 13,
                cursor: 'pointer',
                background: i === highlight ? '#f4f4f4' : 'transparent',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span>{renderLabel(o)}</span>
              {o.count != null && (
                <span style={{ color: '#999', fontSize: 11 }}>{o.count.toLocaleString()}</span>
              )}
            </div>
          ))}
          {filtered.length > visible.length && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: '#999', borderTop: '1px solid #eee' }}>
              +{filtered.length - visible.length} more — keep typing to narrow
            </div>
          )}
        </div>
      )}
      {open && visible.length === 0 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
            background: '#fff', border: '1px solid #dee2e6', padding: '8px 10px',
            fontSize: 12, color: '#999', borderRadius: 4,
          }}
        >
          No matches
        </div>
      )}
    </div>
  );
}
